import React, { useState, useEffect, useMemo } from 'react';
import { auth } from '../../../../firebase/config';
import Modal from '../../../../components/Modal';
import TextField from '../../../../components/TextField';
import Button from '../../../../components/Button';
import Icon from '../../../../components/Icon';
import SearchableSelect from '../../../../components/SearchableSelect';
import { FiCalendar, FiMapPin, FiUser, FiInfo, FiCheckSquare } from 'react-icons/fi';
import { useAppSelector } from '../../../../store';
import { fetchBranchesData, type Branch } from '../../../branches/service/branchesService';
import { createLeaseRenewalSurvey, generateLeaseSurveyId } from '../service/leaseRenewalSurveyService';

type AddLeaseRenewalSurveyModalProps = {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: (message?: string) => void;
};

export default function AddLeaseRenewalSurveyModal({ isOpen, onClose, onSuccess }: AddLeaseRenewalSurveyModalProps) {
    const [loading, setLoading] = useState(false);
    const [branchesLoading, setBranchesLoading] = useState(false);
    const [branches, setBranches] = useState<Branch[]>([]);
    const [errors, setErrors] = useState<Record<string, string>>({});

    const currentUser = useAppSelector((s) => s.user.currentUser);
    const psoProfile = useAppSelector((s) => s.user.pso);
    const bankProfile = useAppSelector((s) => s.user.bank);

    const [surveyId, setSurveyId] = useState('');
    const [requestDate, setRequestDate] = useState('');
    const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);

    // Formatted current date
    useEffect(() => {
        if (isOpen) {
            setSurveyId(generateLeaseSurveyId());
            const today = new Date().toISOString().split('T')[0];
            setRequestDate(today);
            setSelectedBranch(null);
            setErrors({});
            loadBranches();
        }
    }, [isOpen]);

    const loadBranches = async () => {
        const uid = currentUser?.uid;
        if (!uid) return;
        setBranchesLoading(true);
        try {
            const { branches: branchesData } = await fetchBranchesData(
                uid,
                false, // not bank-direct
                psoProfile?.authUid,
                psoProfile?.designation
            );
            // Only keep active branches that have a PSO assigned
            setBranches(branchesData.filter(b => b.isActive !== false));
        } catch (error) {
            console.error('Error fetching branches for selection:', error);
        } finally {
            setBranchesLoading(false);
        }
    };

    const branchOptions = useMemo(() => {
        return branches.map((b) => ({
            id: b.id,
            value: b.id,
            label: `${b.branchCode} — ${b.locationName || 'Unknown City'}`,
            address: b.address || '',
        }));
    }, [branches]);

    const handleBranchChange = (value: string) => {
        const found = branches.find((b) => b.id === value) || null;
        setSelectedBranch(found);
        if (errors.branchCode) {
            setErrors(prev => {
                const copy = { ...prev };
                delete copy.branchCode;
                return copy;
            });
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (loading) return;

        const newErrors: Record<string, string> = {};
        if (!selectedBranch) {
            newErrors.branchCode = 'Please select a branch code';
        }
        if (!requestDate) {
            newErrors.requestDate = 'Please specify a request date';
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        if (!selectedBranch) return;

        if (!selectedBranch.psoId || !selectedBranch.psoName) {
            setErrors({
                branchCode: 'Selected branch does not have an assigned PSO. Please assign a PSO to this branch first.'
            });
            return;
        }

        setLoading(true);
        try {
            const bankDocId = sessionStorage.getItem('bankDocId') || bankProfile?.bankDocId;
            if (!bankDocId) {
                throw new Error('Bank document ID is missing. Please re-authenticate.');
            }

            const surveyData = {
                surveyId,
                createdBy: currentUser?.uid || '',
                createdByName: psoProfile?.name || currentUser?.displayName || 'Incharge MIS',
                status: 'Assigned' as const,
                bankId: bankDocId,
                branchCode: selectedBranch.branchCode,
                branchName: selectedBranch.address || 'N/A',
                branchLocation: selectedBranch.locationName || 'N/A',
                assignedTo: selectedBranch.psoId,
                assignedToName: selectedBranch.psoName,
                requestDate,
            };

            await createLeaseRenewalSurvey(bankDocId, surveyData);
            onSuccess('Lease renewal survey request has been initiated and assigned successfully.');
            onClose();
        } catch (error: any) {
            console.error('Error initiating lease renewal survey:', error);
            setErrors({ submit: error.message || 'Failed to initiate survey request' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Initiate Lease Renewal Request"
            size="md"
            closeOnBackdropClick={false}
            bodyClassName="overflow-visible bg-white dark:bg-gray-800"
            footer={
                <div className="flex justify-end gap-3 px-6 py-4">
                    <Button
                        type="button"
                        variant="secondary"
                        onClick={onClose}
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="primary"
                        disabled={loading}
                        form="initiate-lease-form"
                        className="flex items-center gap-2"
                    >
                        <Icon icon={FiCheckSquare} />
                        {loading ? 'Creating...' : 'Create Request'}
                    </Button>
                </div>
            }
        >
            <form id="initiate-lease-form" onSubmit={handleSubmit} className="p-6 space-y-6">
                {errors.submit && (
                    <div className="rounded-xl bg-red-50 p-4 text-sm text-red-600 dark:bg-red-950/20 dark:text-red-400">
                        {errors.submit}
                    </div>
                )}

                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <TextField
                        label="Survey ID"
                        value={surveyId}
                        disabled
                        required
                        className="bg-gray-50 dark:bg-slate-800"
                    />

                    <div className="flex flex-col">
                        <label className="mb-1 text-xs font-semibold text-gray-700 dark:text-gray-300">
                            Request Date
                        </label>
                        <div className="relative">
                            <input
                                type="date"
                                value={requestDate}
                                onChange={(e) => setRequestDate(e.target.value)}
                                className={`w-full rounded-xl border bg-white py-2.5 px-3 text-sm font-medium text-gray-900 outline-none transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:bg-slate-900 dark:text-white ${
                                    errors.requestDate
                                        ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                                        : 'border-slate-200 dark:border-slate-700'
                                }`}
                                required
                            />
                        </div>
                        {errors.requestDate && (
                            <p className="mt-1 text-xs text-red-500">{errors.requestDate}</p>
                        )}
                    </div>
                </div>

                <div className="flex flex-col">
                    <label className="mb-1.5 text-xs font-semibold text-gray-700 dark:text-gray-300">
                        Select Branch Code
                    </label>
                    <SearchableSelect
                        options={branchOptions}
                        value={selectedBranch ? selectedBranch.id : ''}
                        onChange={handleBranchChange}
                        placeholder={branchesLoading ? 'Loading branches...' : 'Search branch code...'}
                        disabled={branchesLoading || loading}
                    />
                    {errors.branchCode && (
                        <p className="mt-1 text-xs text-red-500">{errors.branchCode}</p>
                    )}
                </div>

                {selectedBranch && (
                    <div className="rounded-2xl border border-slate-100 bg-slate-50/50 p-5 space-y-4 dark:border-slate-800 dark:bg-slate-900/50">
                        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                            <Icon icon={FiInfo} className="h-3.5 w-3.5" />
                            Branch Information Preview
                        </h4>

                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div>
                                <span className="text-[10px] font-semibold text-slate-400 dark:text-slate-500 block uppercase tracking-wider">
                                    Branch Address
                                </span>
                                <span className="text-sm font-semibold text-slate-800 dark:text-slate-200 block mt-0.5">
                                    {selectedBranch.address || 'N/A'}
                                </span>
                            </div>

                            <div>
                                <span className="text-[10px] font-semibold text-slate-400 dark:text-slate-500 block uppercase tracking-wider">
                                    Branch Location / City
                                </span>
                                <span className="text-sm font-semibold text-slate-800 dark:text-slate-200 block mt-0.5 flex items-center gap-1">
                                    <Icon icon={FiMapPin} className="text-blue-500 h-3.5 w-3.5 shrink-0" />
                                    {selectedBranch.locationName || 'N/A'}
                                </span>
                            </div>

                            <div className="sm:col-span-2 border-t border-dashed border-slate-200 dark:border-slate-800 pt-3">
                                <span className="text-[10px] font-semibold text-slate-400 dark:text-slate-500 block uppercase tracking-wider">
                                    Assigned PSO (Assessment Officer)
                                </span>
                                <span className="text-sm font-semibold text-slate-800 dark:text-slate-200 block mt-0.5 flex items-center gap-1.5">
                                    <Icon icon={FiUser} className="text-teal-500 h-4 w-4 shrink-0" />
                                    {selectedBranch.psoName || <span className="text-red-500 italic">No PSO Assigned</span>}
                                </span>
                            </div>
                        </div>
                    </div>
                )}
            </form>
        </Modal>
    );
}
